import { AbsoluteModelPath } from "../../../../../../Common/ModelPath/ModelPath";
import { CalculationFunctionDependency } from "../CalculationFunctionDependency";
import { CalculationTarget, CalculationTargetAttribute } from "../CalculationTarget";
import { IEvaluationContext } from "../IEvaluationContext";
import { MatchKind } from "../../../../../../Common/ModelPath/PathMatcher";

import { CalculationFunctionBase } from "./CalculationFunctionBase";

type CalculationFunctionEvaluator = (evaluationContext: IEvaluationContext) => string | undefined;

export class CalculationFunction extends CalculationFunctionBase {
    private readonly calculationTarget: CalculationTarget;
    private readonly dependencies: readonly CalculationFunctionDependency[];
    private readonly evaluator: CalculationFunctionEvaluator;
    private readonly isDisabled: boolean;
    private readonly isAutoField: boolean;
    private readonly isOptional: boolean;
    private readonly isAutoValueForced: boolean;

    public constructor(
        targetPath: AbsoluteModelPath,
        dependenciesPaths: AbsoluteModelPath[],
        evaluator: CalculationFunctionEvaluator,
        isAutoField: boolean | undefined = undefined,
        isDisabled: boolean | undefined = undefined,
        isOptional: boolean | undefined = undefined,
        isAutoValueForced: boolean | undefined = undefined
    ) {
        super(targetPath);
        this.isOptional = isOptional ?? false;
        this.isDisabled = isDisabled ?? false;
        this.isAutoField = isAutoField ?? false;
        this.isAutoValueForced = isAutoValueForced ?? false;
        this.calculationTarget = new CalculationTarget(targetPath, CalculationTargetAttribute.Value);
        this.dependencies = dependenciesPaths.map(x => new CalculationFunctionDependency(targetPath, x));
        this.evaluator = evaluator;
    }

    public getDependencies(): readonly CalculationFunctionDependency[] {
        return this.dependencies;
    }

    public execute(context: IEvaluationContext): void {
        const evaluator = this.evaluator;
        const targetPath = this.targetPath;
        if (context.isIgnoredPath(targetPath)) {
            return;
        }
        const autoFlag = context.getAutoFlag(targetPath);
        const ignoreAutoFlag = context.getIgnoreAutoFlag(targetPath);
        const evaluationResult = this.checkIfCalculationRequired(context) ? evaluator(context) : undefined;
        if (this.isAutoField) {
            const prevValue = context.get(targetPath);
            const prevAutoValue = context.getAutoValue(targetPath);
            context.setAutoValue(targetPath, evaluationResult);
            const isValueAbsentInSource =
                (!this.isOptional || this.isAutoValueForced) && prevValue == prevAutoValue && autoFlag == undefined;
            if ((!ignoreAutoFlag && (this.isDisabled || autoFlag)) || isValueAbsentInSource) {
                context.set(targetPath, evaluationResult);
            }
        } else {
            context.set(targetPath, evaluationResult);
        }
    }

    public checkIfCalculationRequired(context: IEvaluationContext) {
        const hasDeletedItems = this.dependencies.some(x =>
            Iterator.from(context.relatedEvents).some(
                evt =>
                    x
                        .getDependencyPath()
                        .matchFor(evt.path)
                        .every(
                            match =>
                                match.kind === MatchKind.MatchedPath || match.kind === MatchKind.MatchedMultiplicity
                        ) && evt.type == "remove"
            )
        );

        const isDependsOnEmptySum = () => {
            const dep = this.dependencies[0]?.getDependencyPath();
            const depParts = dep?.getPathPartsAsArray() ?? [];
            return (
                dep != undefined &&
                this.dependencies.length === 1 &&
                depParts[depParts.length - 1] === "Sum" &&
                context.get(dep) === ""
            );
        };

        return !(
            (hasDeletedItems || isDependsOnEmptySum()) &&
            context.getAutoValue(this.targetPath) == undefined &&
            context.get(this.targetPath) == undefined
        );
    }

    public isDependsOn(calculationTarget: CalculationTarget): boolean {
        return (
            calculationTarget.targetAttribute === CalculationTargetAttribute.Value &&
            this.dependencies.some(x => x.isMatchToPath(calculationTarget.modelPath))
        );
    }

    public getTarget(): CalculationTarget {
        return this.calculationTarget;
    }

    public asDebugString(): string {
        return "CalculationFunction(N/A)";
    }
}
